import { forEach, reduce, isString, has } from 'lodash';
import { POST } from '../constants/requestMethods';
import { ROUTE_OAUTH2_AUTH } from '../constants/pathnames';
import { getCurrentUtcTime } from '../lib/DateUtils';
import { getCorsCompatibleURL } from '../lib/URLUtils';
import { generateCodeVerifier, generateCodeChallenge } from './utils/oidc';

const REDIRECT_URI = window.location.protocol + '//' + window.location.host + ROUTE_OAUTH2_AUTH;
const AUTH_URL_PARAM_PREFIX = 'oauth2.auth_url_param.';
const TOKEN_URL_PARAM_PREFIX = 'oauth2.token_url_param.';

// Feature flags reach us as hidden auth *parameters* (config: { isHiddenField: true,
// default: "true" }), resolved into credInfo like any other parameter — never as
// auth_template.config keys, which no negotiation path can read. Two naming layers are
// in play and should not be mixed:
//
//   underscore params (`oauth2.pkce_enabled`, `oauth2.client_id`)  — read here
//   dot config keys  (`oauth2.client.id`, `oauth2.client.auth.scheme`) — the credential
//                                                                       contract, read
//                                                                       downstream on refresh
const PKCE_ENABLED_FIELD = 'oauth2.pkce_enabled';
const PKCE_CHALLENGE_METHOD = 'S256';
const PKCE_CODE_CHALLENGE_PARAM = 'code_challenge';
const PKCE_CODE_CHALLENGE_METHOD_PARAM = 'code_challenge_method';
const PKCE_CODE_VERIFIER_PARAM = 'code_verifier';

// HTTP Basic client authentication on the token endpoint (client_secret_basic,
// RFC 6749 §2.3.1) — required by Reddit, Spotify, Xero, Intuit, PayPal and Zoom.
// `oauth2.basic_auth` carries a pre-encoded pair the user pasted by hand and keeps
// working; `oauth2.client_basic_auth_enabled` lets a template ask us to derive it instead,
// so the user only ever enters an ID and a secret.
//
// This only ever *adds* a header. What travels in the token body stays entirely
// under the template's control, via the `oauth2.token_url_param.*` params it declares
// — so a provider that rejects credentials in both places is handled by omitting them
// from the template, not by special-casing here.
const BASIC_AUTH_HEADER_FIELD = 'oauth2.basic_auth';
const CLIENT_BASIC_AUTH_ENABLED_FIELD = 'oauth2.client_basic_auth_enabled';

// Canonical first, then the older per-URL declarations, so templates predating the
// canonical names keep resolving. Core connectors (GDrive, Box, BigQuery, …) build
// credInfo with the auth_url_param/token_url_param spellings.
const CLIENT_ID_FIELDS = [
  'oauth2.client_id',
  'oauth2.token_url_param.client_id',
  'oauth2.auth_url_param.client_id',
];
const CLIENT_SECRET_FIELDS = ['oauth2.client_secret', 'oauth2.token_url_param.client_secret'];

function isFlagEnabled(credInfo, fieldName) {
  const value = credInfo && credInfo[fieldName];

  if (value === true) {
    return true;
  }

  // Parameter defaults arrive as strings; booleans are accepted too.
  return isString(value) && value.trim().toLowerCase() === 'true';
}

function isPkceEnabled(credInfo) {
  return isFlagEnabled(credInfo, PKCE_ENABLED_FIELD);
}

function resolveDeclaredValue(credInfo, fieldNames) {
  for (let i = 0; i < fieldNames.length; i++) {
    const value = evaluateMacros(credInfo[fieldNames[i]], credInfo);

    if (isString(value) && value !== '') {
      return value;
    }
  }

  return null;
}

/**
 * base64 of the raw `id:secret` pair, matching the `curl -u id:secret` request Reddit
 * accepts per the reproduction on NEX-22112. RFC 6749 §2.3.1 describes form-urlencoding
 * each half first, but providers in practice expect the raw pair.
 */
function encodeBasicCredentials(clientId, clientSecret) {
  return window.btoa(`${clientId}:${clientSecret}`);
}

function getBasicAuthCredentials(credInfo) {
  // A pre-encoded value is an explicit override and wins, so templates already
  // carrying `oauth2.basic_auth` behave exactly as they do today.
  if (credInfo[BASIC_AUTH_HEADER_FIELD]) {
    return credInfo[BASIC_AUTH_HEADER_FIELD];
  }

  if (!isFlagEnabled(credInfo, CLIENT_BASIC_AUTH_ENABLED_FIELD)) {
    return null;
  }

  const clientId = resolveDeclaredValue(credInfo, CLIENT_ID_FIELDS);
  const clientSecret = resolveDeclaredValue(credInfo, CLIENT_SECRET_FIELDS);

  if (!clientId || !clientSecret) {
    return null;
  }

  return encodeBasicCredentials(clientId, clientSecret);
}

function authenticate(fn, credInfo) {
  const pkceEnabled = isPkceEnabled(credInfo);
  // One verifier per attempt, replayed on the token exchange from this closure, so
  // nothing PKCE-related is persisted on the credential.
  const codeVerifier = pkceEnabled ? generateCodeVerifier() : null;

  window.HandlePopupResult = function (authResponse, error) {
    delete window.HandlePopupResult;

    const codeProperty = credInfo['oauth2.auth_response.code'] || 'code';
    let accessError = null;

    if (error) {
      accessError = error;
    } else if (has(authResponse, 'error')) {
      accessError = {
        code: authResponse.error,
        description: has(authResponse, 'error_description')
          ? authResponse.error_description
          : authResponse.error,
      };
    } else if (has(authResponse, 'access_token')) {
      fn({ ...authResponse, authorizedAt: getCurrentUtcTime() });
    } else if (has(authResponse, codeProperty)) {
      const credTokenUrl = evaluateMacros(credInfo['oauth2.token_url'], credInfo);
      const tokenURL = getCorsCompatibleURL(credTokenUrl);

      const tokenParams = getParamsFromCredInfo(
        credInfo,
        TOKEN_URL_PARAM_PREFIX,
        // Deliberately not encoded, unlike every declared param below. JSONUtils
        // .queryStringToJSON already decoded this value, so the round trip is asymmetric
        // and a code containing `+` reaches the provider as a space. Left alone here
        // because correcting it changes the request for every connector at once, and no
        // provider has been observed hitting it — tracked separately.
        ['code=' + authResponse[codeProperty]],
        codeVerifier ? { [PKCE_CODE_VERIFIER_PARAM]: codeVerifier } : null,
      );
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        Accept: 'application/json',
      };

      const basicAuth = getBasicAuthCredentials(credInfo);

      if (basicAuth) {
        headers.Authorization = `Basic ${basicAuth}`;
      }

      fetch(tokenURL, {
        method: POST,
        body: tokenParams,
        headers,
      })
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            return response.json();
          }
          throw new Error(response.statusText);
        })
        .then((tokenResponse) => {
          fn({
            ...authResponse,
            ...tokenResponse,
            authorizedAt: getCurrentUtcTime(),
          });
        })
        .catch(console.log);
    }

    if (accessError) {
      fn(null, accessError);
    }
  };

  const authURL =
    evaluateMacros(credInfo['oauth2.auth_url'], credInfo) +
    '?' +
    getParamsFromCredInfo(
      credInfo,
      AUTH_URL_PARAM_PREFIX,
      null,
      codeVerifier
        ? {
            [PKCE_CODE_CHALLENGE_PARAM]: generateCodeChallenge(codeVerifier),
            [PKCE_CODE_CHALLENGE_METHOD_PARAM]: PKCE_CHALLENGE_METHOD,
          }
        : null,
    );
  window.open(authURL, 'Authorization', 'top=300,left=250,height=600,width=600');
}

function getParamsFromCredInfo(credInfo, paramPrefix, extraParams = null, pkceParams = null) {
  let params = ['redirect_uri=' + encodeURIComponent(REDIRECT_URI)];

  if (extraParams) {
    params = params.concat(extraParams);
  }

  const declaredParamNames = {};

  forEach(credInfo, (value, key) => {
    if (!key.startsWith(paramPrefix)) {
      return;
    }

    const paramName = key.substring(paramPrefix.length);
    const paramValue = evaluateMacros(value, credInfo);

    declaredParamNames[paramName] = true;
    params.push(paramName + '=' + encodeURIComponent(paramValue));
  });

  // Appended only when the template does not declare them itself, so a template
  // stays in control and no parameter is duplicated. No-op when PKCE is off.
  forEach(pkceParams, (value, paramName) => {
    if (declaredParamNames[paramName]) {
      return;
    }

    params.push(paramName + '=' + encodeURIComponent(value));
  });

  return params.join('&');
}

function evaluateMacros(value, data) {
  if (isString(value)) {
    // evaluate macros in the param value
    const macros = value.match(/\$\{([\w.]+)\}/g);
    return reduce(
      macros,
      (acc, macro) => {
        const macroParamName = macro.slice(2, -1);
        return acc.replace(macro, data[macroParamName]);
      },
      value,
    );
  }

  return value;
}

export default { authenticate };
